import type {
  TGuaranteesApplicationFile,
  TGuaranteesApplicationFileCapability,
  TGuaranteesApplicationFileErrorCode,
  TGuaranteesApplicationFileType,
  TPatchGuaranteesApplicationFileCapabilityPayload,
} from '~/store/guarantees/types.ts';
import {
  GUARANTEES_APPLICATION_FILE_CAPABILITY_KEYS,
  GUARANTEES_APPLICATION_FILE_ERROR_CODES,
} from './constants.ts';
import type { TGuaranteesApplicationFileAction } from './types.ts';

const APPLICATION_FILE_ACTION_CAPABILITY_MAP: Record<
  TGuaranteesApplicationFileAction,
  keyof TPatchGuaranteesApplicationFileCapabilityPayload
> = {
  download: 'canDownload',
  upload: 'canUpload',
  regenerate: 'canRegenerate',
  delete: 'canDelete',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringValue = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const getNestedErrorCode = (value: unknown): string | null => {
  if (!isRecord(value)) {
    return null;
  }

  return (
    getStringValue(value.errorCode) ??
    getNestedErrorCode(value.data) ??
    getNestedErrorCode(value.payload) ??
    getNestedErrorCode(value.response)
  );
};

export const getGuaranteesApplicationFileErrorCode = (
  error: unknown
): TGuaranteesApplicationFileErrorCode | null => {
  const errorCode = getNestedErrorCode(error);
  const supportedErrorCodes = Object.values(GUARANTEES_APPLICATION_FILE_ERROR_CODES);

  return supportedErrorCodes.includes(errorCode as TGuaranteesApplicationFileErrorCode)
    ? (errorCode as TGuaranteesApplicationFileErrorCode)
    : null;
};

export const isGuaranteesApplicationFileError = (
  error: unknown,
  errorCode: TGuaranteesApplicationFileErrorCode
): boolean => getGuaranteesApplicationFileErrorCode(error) === errorCode;

export const isApplicationFileActionNotAllowedError = (error: unknown): boolean =>
  isGuaranteesApplicationFileError(
    error,
    GUARANTEES_APPLICATION_FILE_ERROR_CODES.FILE_ACTION_NOT_ALLOWED
  );

export const isApplicationFileAlreadyExistsError = (error: unknown): boolean =>
  isGuaranteesApplicationFileError(
    error,
    GUARANTEES_APPLICATION_FILE_ERROR_CODES.APPLICATION_FILE_ALREADY_EXISTS_USE_FILE_ID
  );

export const isApplicationFileNotFoundError = (error: unknown): boolean =>
  isGuaranteesApplicationFileError(
    error,
    GUARANTEES_APPLICATION_FILE_ERROR_CODES.APPLICATION_FILE_NOT_FOUND
  );

export const isApplicationFileRegenerationUnsupportedError = (error: unknown): boolean =>
  isGuaranteesApplicationFileError(
    error,
    GUARANTEES_APPLICATION_FILE_ERROR_CODES.FILE_TYPE_REGENERATION_NOT_SUPPORTED
  );

export const hasApplicationFileCapabilityPatch = (
  payload: TPatchGuaranteesApplicationFileCapabilityPayload
): boolean =>
  GUARANTEES_APPLICATION_FILE_CAPABILITY_KEYS.some((key) => typeof payload[key] === 'boolean');

export const getApplicationFileCapabilityFiles = (
  capability?: TGuaranteesApplicationFileCapability | null
): TGuaranteesApplicationFile[] => (Array.isArray(capability?.files) ? capability.files : []);

export const getFirstApplicationFile = (
  capability?: TGuaranteesApplicationFileCapability | null
): TGuaranteesApplicationFile | null => getApplicationFileCapabilityFiles(capability)[0] ?? null;

export const getApplicationFileId = (
  file?: TGuaranteesApplicationFile | null
): string | null =>
  getStringValue(file?.fileId) ?? getStringValue(file?.id) ?? getStringValue(file?.applicationFileId);

export const getApplicationFileReplacementFileId = (
  capability?: TGuaranteesApplicationFileCapability | null,
  preferredFile?: TGuaranteesApplicationFile | null
): string | null =>
  getApplicationFileId(preferredFile) ?? getApplicationFileId(getFirstApplicationFile(capability));

export const findApplicationFileCapability = (
  fileTypes: TGuaranteesApplicationFileCapability[],
  fileType: TGuaranteesApplicationFileType
): TGuaranteesApplicationFileCapability | null =>
  fileTypes.find((capability) => capability.fileType === fileType) ?? null;

export const getApplicationFileCapabilityDisplayName = (
  capability: TGuaranteesApplicationFileCapability
): string => getStringValue(capability.displayName) ?? capability.fileType;

export const canShowApplicationFileAction = (
  capability: TGuaranteesApplicationFileCapability,
  action: TGuaranteesApplicationFileAction
): boolean => Boolean(capability[APPLICATION_FILE_ACTION_CAPABILITY_MAP[action]]);

export const getApplicationFileDisplayName = (
  file: TGuaranteesApplicationFile,
  fallbackIndex: number,
  fallbackDisplayName?: string | null
): string =>
  getStringValue(file.description) ??
  getStringValue(fallbackDisplayName) ??
  `Файл ${fallbackIndex + 1}`;

export const normalizeGuaranteesApplicationFileCapabilities = (
  response?: { fileTypes?: TGuaranteesApplicationFileCapability[] } | null
): TGuaranteesApplicationFileCapability[] =>
  (Array.isArray(response?.fileTypes) ? response.fileTypes : []).map((capability) => ({
    ...capability,
    files: getApplicationFileCapabilityFiles(capability),
  }));
